import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

// Thunk for Credit and Debit amount
export const creditAndDebitAmount = createAsyncThunk(
  "creditAndDebitAmount",
  async ({ entity_id, period }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/statistics/statement/credit_and_debit_amounts`,
        {
          params: {
            entity_id,
            period,
          },
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Thunk for Idle funds
export const idleFunds = createAsyncThunk(
  "idleFunds",
  async ({ owner_acct_id, period }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/statistics/statement/${owner_acct_id}/idle_funds`,
        {
          params: { period },
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching idle funds:", error);
      return rejectWithValue(error);
    }
  },
);

// Thunk for OD Interest Saved
export const OdInterestSavedThunk = createAsyncThunk(
  "OdInterestSavedThunk",
  async ({ entity_id, owner_acct_ids, period }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/statistics/generate_on_idle_fund_report`,
        {
          params: {
            entity_id,
            period,
            owner_account_ids: owner_acct_ids,
          },
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Thunk for Bene Transaction Count

export const beneTransactionCount = createAsyncThunk(
  "beneTransactionCount",
  async ({ entity_id, period }, { rejectWithValue }) => {
    try {
      const url = `${baseUrl}/statistics/beneficiary/transaction_count?entity_id=${entity_id}&period=${period}`;

      const response = await axiosInstance.get(url, {});

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Thunk for Internal Transaction Count
export const internalTransactionCount = createAsyncThunk(
  "internalTransactionCount",
  async ({ entity_id, period }, { rejectWithValue }) => {
    try {
      const url = `${baseUrl}/statistics/internal/transaction_count?entity_id=${entity_id}&period=${period}`;

      const response = await axiosInstance.get(url, {});

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Thunk for Productivity Tracker
export const ProductivityTrackerThunk = createAsyncThunk(
  "ProductivityTrackerThunk",
  async ({ entity_id, user_ids, period, reportType }, { rejectWithValue }) => {
    try {
      const endpoint =
        reportType === "internal"
          ? "/statistics/internal/user_productivity_tracker"
          : "/statistics/beneficiary/user_productivity_tracker";

      const response = await axiosInstance.get(`${baseUrl}${endpoint}`, {
        params: {
          entity_id,
          period,
          "user_ids[]": user_ids,
        },
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error while fetching data:", error);
      return rejectWithValue(error);
    }
  },
);

// Thunk for Cash Coverage Period
export const CashCoveragePeriodThunk = createAsyncThunk(
  "CashCoveragePeriodThunk",
  async ({ entity_id }, { rejectWithValue }) => {
    try {
      const url = `${baseUrl}/statistics/statement/cash_coverage_report?entity_id=${entity_id}`;

      const response = await axiosInstance.get(url, {
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": sessionStorage.getItem("q2p_token"),
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Slice
const settingStatistics = createSlice({
  name: "settingStatistics",
  initialState: {
    // States for Credit and Debit Amount
    creditAndDebitAmountLoading: false,
    creditAndDebitAmountData: null,
    creditAndDebitAmountError: null,

    // States for Idle Funds
    idleFundsLoading: false,
    idleFundsData: null,
    idleFundsError: null,

    // States for OD Interest Saved
    OdInterestSavedLoading: false,
    OdInterestSavedData: null,
    OdInterestSavedError: null,

    // States for Bene Transaction Count
    beneTransactionCountLoading: false,
    beneTransactionCountData: null,
    beneTransactionCountError: null,

    // States for Internal Transaction Count
    internalTransactionCountLoading: false,
    internalTransactionCountData: null,
    internalTransactionCountError: null,

    // States for Productivity Tracker Thunk
    ProductivityTrackerThunkLoading: false,
    ProductivityTrackerThunkData: null,
    ProductivityTrackerThunkError: null,

    // States for Cash Coverage Period
    CashCoveragePeriodThunkLoading: false,
    CashCoveragePeriodThunkData: null,
    CashCoveragePeriodThunkError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(creditAndDebitAmount.pending, (state) => {
        state.creditAndDebitAmountLoading = true;
        state.creditAndDebitAmountError = null;
      })
      .addCase(creditAndDebitAmount.fulfilled, (state, action) => {
        state.creditAndDebitAmountLoading = false;
        state.creditAndDebitAmountData = action.payload;
      })
      .addCase(creditAndDebitAmount.rejected, (state, action) => {
        state.creditAndDebitAmountLoading = false;
        state.creditAndDebitAmountError = action.payload?.status;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      })

      // Cases for Idle Funds
      .addCase(idleFunds.pending, (state) => {
        state.idleFundsLoading = true;
        state.idleFundsError = null;
      })
      .addCase(idleFunds.fulfilled, (state, action) => {
        state.idleFundsLoading = false;
        state.idleFundsData = action.payload;
      })
      .addCase(idleFunds.rejected, (state, action) => {
        state.idleFundsLoading = false;
        state.idleFundsError = action.payload?.status || true;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      })

      // Cases for OD Interest Saved
      .addCase(OdInterestSavedThunk.pending, (state) => {
        state.OdInterestSavedLoading = true;
        state.OdInterestSavedError = null;
      })
      .addCase(OdInterestSavedThunk.fulfilled, (state, action) => {
        state.OdInterestSavedLoading = false;
        state.OdInterestSavedData = action.payload;
      })
      .addCase(OdInterestSavedThunk.rejected, (state, action) => {
        state.OdInterestSavedLoading = false;
        state.OdInterestSavedError = action.payload?.status || true;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      })

      // Cases for Bene Transaction Count
      .addCase(beneTransactionCount.pending, (state) => {
        state.beneTransactionCountLoading = true;
        state.beneTransactionCountError = null;
      })
      .addCase(beneTransactionCount.fulfilled, (state, action) => {
        state.beneTransactionCountLoading = false;
        state.beneTransactionCountData = action.payload;
      })
      .addCase(beneTransactionCount.rejected, (state, action) => {
        state.beneTransactionCountLoading = false;
        state.beneTransactionCountError = action.payload?.status || true;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      })

      // Cases for Internal Transaction Count
      .addCase(internalTransactionCount.pending, (state) => {
        state.internalTransactionCountLoading = true;
        state.internalTransactionCountError = null;
      })
      .addCase(internalTransactionCount.fulfilled, (state, action) => {
        state.internalTransactionCountLoading = false;
        state.internalTransactionCountData = action.payload;
      })
      .addCase(internalTransactionCount.rejected, (state, action) => {
        state.internalTransactionCountLoading = false;
        state.internalTransactionCountError = action.payload?.status || true;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      })

      // Cases for Productivity tracker
      .addCase(ProductivityTrackerThunk.pending, (state) => {
        state.ProductivityTrackerThunkLoading = true;
        state.ProductivityTrackerThunkError = null;
      })
      .addCase(ProductivityTrackerThunk.fulfilled, (state, action) => {
        state.ProductivityTrackerThunkLoading = false;
        state.ProductivityTrackerThunkData = action.payload;
      })
      .addCase(ProductivityTrackerThunk.rejected, (state, action) => {
        state.ProductivityTrackerThunkLoading = false;
        state.ProductivityTrackerThunkError = action.payload?.status || true;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      })

      // Cases for Cash Coverage Period
      .addCase(CashCoveragePeriodThunk.pending, (state) => {
        state.CashCoveragePeriodThunkLoading = true;
        state.CashCoveragePeriodThunkError = null;
      })
      .addCase(CashCoveragePeriodThunk.fulfilled, (state, action) => {
        state.CashCoveragePeriodThunkLoading = false;
        state.CashCoveragePeriodThunkData = action.payload;
      })
      .addCase(CashCoveragePeriodThunk.rejected, (state, action) => {
        state.CashCoveragePeriodThunkLoading = false;
        state.CashCoveragePeriodThunkError = action.payload?.status || true;
        if (action?.payload?.status !== 404) {
          CommonToast({
            text: "Failed to fetch.",
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          });
        }
      });
  },
});

export default settingStatistics.reducer;
